import React, { useState } from 'react';
import { BaseModal, Button, Input, Switch } from '@cokitchen/cokitchen-components';
import { ReactComponent as BoltIcon } from 'assets/icons/update-surge-modal-icon.svg';
import { BrandType } from 'models/brand.model';
import DurationButton from './duration-button';
import ConfirmTrafficModal from './confirm-traffic-modal';
import { apiService } from 'utils/api';
import moment from 'moment';
import { CoKitchenType } from 'models/cokitchen.model';
import TrafficSuccessModal from './success-modal';
import { DEFAULT_TRAFFIC_SHUTDOWN_DURATIONS } from 'utils/constants';
import useDashboardContext from 'contexts/dashboard.context';

type BrandProps = {
  type: 'brand';
  open: boolean;
  brand: BrandType;
  onClose: () => void;
  afterMutation: (is_busy: boolean, reactivation_time: string) => void;
};

type CokitchenProps = {
  type: 'cokitchen';
  open: boolean;
  cokitchen: CoKitchenType;
  onClose: () => void;
  afterMutation: (is_busy: boolean, reactivation_time: string) => void;
};

type PayloadDataType = {
  reactivation_time?: string | null;
  is_busy: boolean;
};

type Props = BrandProps | CokitchenProps;

const ManageTrafficModal = (props: Props) => {
  const { type, open, onClose, afterMutation } = props;
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [activeDuration, setActiveDuration] = useState<number | null>(null);
  const [datetime, setDatetime] = useState({ date: '', time: '' });
  const [loading, setLoading] = useState(false);
  const { shouldRefetchBrands, setShouldRefetchBrands } = useDashboardContext();
  const is_busy = props.type === 'brand' ? props.brand.is_busy : props.cokitchen.is_busy;
  const [isBusy, setIsBusy] = useState(is_busy);

  const name = type === 'brand' ? props.brand?.name : props.cokitchen?.name;

  const date = moment(datetime.date || undefined);
  const dateIsToday = date.format('YYYY-MM-DD') === moment().format('YYYY-MM-DD');
  const minTime = dateIsToday ? moment().format('HH:mm') : date.format('HH:mm');
  const maxTime = date.endOf('day').format('HH:mm');

  const changeDatetime = (field: 'date' | 'time', value: string) => {
    setActiveDuration(null);
    const newDateTime = { ...datetime, [field]: value };
    if (field === 'date') newDateTime.time = '';
    setDatetime(newDateTime);
  };

  const changeDuration = (duration: number | null) => {
    if (duration) setDatetime({ date: '', time: '' });
    setActiveDuration(duration);
  };

  const turnOff = async () => {
    setLoading(true);
    try {
      const reactivation_time = activeDuration
        ? moment().add(activeDuration, 'minutes').format()
        : Object.values(datetime).every(Boolean)
        ? moment(`${datetime.date} ${datetime.time}`).format()
        : null;
      const data: PayloadDataType = { reactivation_time, is_busy: true };
      if (!data.reactivation_time) data.reactivation_time = null;
      if (type === 'brand') {
        const res = await apiService.put(`traffic-control/brand/${props.brand.id}`, {
          data,
          baseUrl: process.env.REACT_APP_KITCHEN_API_URL
        });
        afterMutation(res?.data?.data?.is_busy, res?.data?.data?.reactivation_time);
        setShouldRefetchBrands(shouldRefetchBrands + 1);
      } else {
        const res = await apiService.put(`traffic-control/cokitchen/${props.cokitchen.id}`, {
          data,
          baseUrl: process.env.REACT_APP_KITCHEN_API_URL
        });
        afterMutation(res?.data?.data?.is_busy, res?.data?.data?.reactivation_time);
      }
      setShowSuccessModal(true);
    } finally {
      setLoading(false);
    }
  };

  const turnOn = async () => {
    setLoading(true);
    const data = { is_busy: false, reactivation_time: null };
    try {
      if (type === 'brand') {
        const res = await apiService.put(`traffic-control/brand/${props.brand.id}`, {
          data,
          baseUrl: process.env.REACT_APP_KITCHEN_API_URL
        });
        afterMutation(res?.data?.data?.is_busy, res?.data?.data?.reactivation_time);
      } else {
        const res = await apiService.put(`traffic-control/cokitchen/${props.cokitchen.id}`, {
          data,
          baseUrl: process.env.REACT_APP_KITCHEN_API_URL
        });
        afterMutation(res?.data?.data?.is_busy, res?.data?.data?.reactivation_time);
      }
      setShouldRefetchBrands(shouldRefetchBrands + 1);
      setShowSuccessModal(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal
      isOpen={open}
      title={`Manage Traffic for ${name}`}
      description='Select the shutdown duration'
      titleIcon={<BoltIcon />}
      titleIconColor='info'
      onRequestClose={onClose}
      footer={
        <Button
          variant='block'
          color='primary'
          //disabled={!activeDuration && !Object.values(datetime).every(Boolean)}
          onClick={() => setShowConfirmModal(true)}>
          Confirm
        </Button>
      }>
      <div className='flex items-center justify-between gap-3 mb-6'>
        <p className='font-medium'>Busy</p>
        <Switch active={isBusy} onChange={setIsBusy} />
      </div>
      {isBusy ? (
        <div className='flex flex-col gap-6'>
          <div className='flex flex-col gap-5'>
            <p className='font-medium'>Select shutdown duration</p>
            <div className='w-full grid gap-x-8 gap-y-6 grid-cols-[repeat(auto-fill,minmax(120px,1fr))]'>
              {DEFAULT_TRAFFIC_SHUTDOWN_DURATIONS.map((duration) => (
                <DurationButton
                  {...duration}
                  key={duration.value}
                  active={duration.value === activeDuration}
                  onChange={(v) => changeDuration(v)}
                />
              ))}
            </div>
          </div>

          <div className='flex flex-col gap-3'>
            <p className='font-medium'>Custom shutdown duration</p>
            <div className='flex items-center gap-6'>
              <Input
                small
                type='date'
                min={moment().format('YYYY-MM-DD')}
                value={datetime.date}
                onChange={(e) => changeDatetime('date', e.target.value)}
              />
              <Input
                small
                type='time'
                min={minTime}
                max={maxTime}
                value={datetime.time}
                onChange={(e) => {
                  if (e.target.validity.valid) changeDatetime('time', e.target.value);
                }}
              />
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmTrafficModal
        title={`Turn ${isBusy ? 'off' : 'on'} ${name}?`}
        description={
          isBusy
            ? `This action stops customers from being able to order from ${
                type === 'brand' ? 'this brand' : 'brands in this location'
              }. It will be automatically turned back on at the time set.`
            : `Once confirmed, orders for this ${
                type === 'brand' ? 'brand' : 'location'
              } will be accepted.`
        }
        open={showConfirmModal}
        loading={loading}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={isBusy ? turnOff : turnOn}
      />

      <TrafficSuccessModal
        title={`${name} has been turned ${isBusy ? 'off' : 'on'}`}
        open={showSuccessModal}
        onClose={onClose}
      />
    </BaseModal>
  );
};

export default ManageTrafficModal;
